import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { BaseLink } from "shared/components/common/base-link";
import { fontMerriweather, fontRobotoCondensed, theme, transientOptions } from "shared/lib/styles";

import { ContentCoverImage } from "scmp-app/components/content/content-cover-image";
import { Figure, StyledFigcaption } from "scmp-app/components/content/content-cover-image/styles";
import { SponsorHeadline as ContentSponsorHeadlineSpan } from "scmp-app/components/content/content-headline/styles";
import { ContentItemHomeSecondary } from "scmp-app/components/content/content-item-render/variants/home-secondary";
import { Headline as ContentItemHomeSecondaryHeadline } from "scmp-app/components/content/content-item-render/variants/home-secondary/styles";
import { SectionLink } from "scmp-app/components/content/content-sections/styles";
import { StyledTopicLink } from "scmp-app/components/content/content-topic-link/styles";
import { EntityLink } from "scmp-app/components/entity-link";
import { HomeFollowButton } from "scmp-app/components/home/follow-button";
import { StyledLi } from "scmp-app/components/schema-render/content/styles";

const itemBorderStyle = css`
  margin-block-end: 20px;
  padding-block-end: 20px;
  border-block-end: 1px solid rgba(0, 0, 0, 0.1);
`;

export const Container = styled.div`
  display: grid;
  grid:
    "leading-image" min-content
    "series" min-content
    "primary" min-content
    "rest" min-content
    / 1fr;

  ${theme.breakpoints.up("tablet")} {
    grid:
      "leading-image leading-image" min-content
      "series series" min-content
      "primary rest" min-content
      / 1fr calc((100% - 64px) / 3);

    column-gap: 32px;
  }
`;

export const StyledEntityLink = styled(EntityLink)`
  display: block;

  color: #000000;
`;

export const TopicContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const Topic = styled.div`
  ${StyledTopicLink} {
    color: #001246;
    font-weight: 700;
    font-size: 15px;
    font-family: ${fontRobotoCondensed};
    font-style: normal;
    line-height: 18px;
  }
`;

export const SectionContainer = styled.div``;

export const Section = styled.div`
  ${SectionLink} {
    color: #4585ff;
    font-size: 13px;
  }
`;

export const LeadingImage = styled.div`
  grid-area: leading-image;

  margin-block-end: 16px;

  ${theme.breakpoints.up("tablet")} {
    position: relative;

    margin-block-end: 24px;

    overflow: hidden;
    aspect-ratio: 250/99;
    ${Figure} {
      position: absolute;

      block-size: auto;
    }
  }
`;

export const StyledContentCoverImage = styled(ContentCoverImage)``;

export const StyledHomeFollowButton = styled(HomeFollowButton)``;

export const Headline = styled.div`
  margin-block-end: 12px;

  font-weight: 300;
  font-size: 24px;
  font-family: ${fontMerriweather};
  line-height: 140%;

  ${ContentSponsorHeadlineSpan} {
    font-weight: 700;
    line-height: 22.4px;
  }

  ${theme.breakpoints.up("tablet")} {
    font-size: 28px;
  }
`;

export const Summary = styled.div`
  display: none;

  &:empty {
    margin: 0;
  }

  ${theme.breakpoints.up("tablet")} {
    display: block;

    margin-block-end: 12px;

    color: #6f6f6f;
    font-weight: 400;
    font-size: 14px;
    line-height: 18.2px;
  }
`;

export const SubHeadline = styled.div`
  color: #6f6f6f;
  font-size: 14px;
  line-height: 18.2px;

  ${StyledLi} {
    margin-block-end: 12px;
  }

  &:empty {
    margin: 0;
  }
`;

export const ActionBar = styled.div`
  time {
    color: #999999;
    font-size: 12px;
  }
`;

type StyledCoverEntityLinkProps = {
  $isSponsorArticle?: boolean;
};
export const StyledCoverEntityLink = styled(EntityLink, {
  ...transientOptions,
})<StyledCoverEntityLinkProps>`
  display: ${props => (props.$isSponsorArticle ? "block" : "none")};

  margin-block-start: 12px;

  ${theme.breakpoints.up("tablet")} {
    display: block;
  }
`;

export const CoverImage = styled.div`
  inline-size: 120px;
  block-size: 80px;

  ${StyledFigcaption} {
    ${theme.breakpoints.up("tablet")} {
      line-height: 14px;
    }
  }
`;

export const Series = styled.div`
  grid-area: series;

  display: flex;
  gap: 8px;

  margin-block-end: 16px;
  ${theme.breakpoints.up("tablet")} {
    margin-block-end: 24px;
  }
`;

export const Primary = styled.div`
  grid-area: primary;
  ${itemBorderStyle};
  ${theme.breakpoints.up("tablet")} {
    margin-block-end: 0;
    padding-block-end: 0;

    border: none;
  }
`;

export const StyledContentItemHomeSecondary = styled(ContentItemHomeSecondary)``;

export const Rest = styled.div`
  grid-area: rest;

  display: flex;
  flex-direction: column;

  ${StyledContentItemHomeSecondary} {
    &:not(:last-child) {
      margin-block-end: 20px;
      padding-block-end: 20px;
      border-block-end: 1px solid rgba(0, 0, 0, 0.1);
    }

    ${ContentItemHomeSecondaryHeadline} {
      font-size: 15px;
      line-height: 21px;
    }
  }
`;

export const TopicLabel = styled.div`
  ${StyledTopicLink} {
    color: #4585ff;
    font-weight: 700;
    font-size: 13px;
    font-family: ${fontRobotoCondensed};
    text-transform: uppercase;
  }
`;

export const LiveTagContainer = styled.div``;

export const SCMPSeriesLink = styled(BaseLink)`
  flex-shrink: 0;
  align-self: center;

  padding-block-end: 2px;

  color: #2c4692;
  font-weight: 400;
  font-size: 15px;
  font-family: ${fontMerriweather};
  font-style: italic;
  line-height: normal;
  border-block-end: 1px dashed #2c3792;
`;

export const SeriesLink = styled(EntityLink)`
  color: #2c4692;
  font-weight: 700;
  font-size: 15px;
  font-family: ${fontMerriweather};
  font-style: italic;
  line-height: normal;
`;
